import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getBlob, ref } from 'firebase/storage';
import JSZip from 'jszip';
import { auth, db, storage } from '../config/firebase';
import {
  buildBwaFileName,
  createVorabkundeFromIntake,
  deleteBwaFilesFromStorage,
  deleteBwaUpload,
  deleteNeukundenIntake,
  notifyIntakeErledigt,
  parseIntakeNettopreis,
  personFileBase,
  suggestTarifFromPackage,
  updateNeukundenIntake,
} from '../services/neukundenIntakeService';
import type {
  BwaUploadDoc,
  IntakePackageType,
  IntakeRechtsform,
  IntakeStatus,
  IntakeTarifTier,
  NeukundenIntakeDoc,
} from '../types/intake';
import useAppStore from '../store/appStore';
import PasswordPrompt from '../components/PasswordPrompt';
import { resolveCoachInitialLetter } from '../utils/mandantNrFormat';
import { getAssignableTeam, mergeTeamPool } from '../utils/teamResolve';
import { isValidIban, normalizeIban } from '../utils/pe-papers/ibanValidation';

const BEARBEITER = ['Christian', 'Martina', 'Jean', 'Nelly'] as const;
/** Coach-Passwort wie Lohnfaktor / Änderungszentrale — Stammdaten-Korrektur */
const INTAKE_EDIT_PASSWORD = '90';
/** Standard-MwSt DE für Brutto-Anzeige (Abbuchung) */
const INTAKE_MWST_PCT = 19;
const FILTERS: { id: 'alle' | IntakeStatus; label: string }[] = [
  { id: 'alle', label: 'Alle' },
  { id: 'neu', label: 'Neu' },
  { id: 'in_bearbeitung', label: 'In Bearbeitung' },
  { id: 'abgeschlossen', label: 'Erledigt' },
];

const TARIF_OPTIONS: { id: IntakeTarifTier; label: string }[] = [
  { id: 'solo', label: 'Solo' },
  { id: 'team', label: 'Team' },
];

const paketLabel = (id: string) =>
  id === 'insider' ? 'INSIDER' : id === 'insider_solo' ? 'INSIDER Solo' : id === 'basic' ? 'BASIC' : id;

function waLink(raw: string): string {
  const digits = String(raw || '').replace(/[^\d]/g, '');
  return digits ? `https://wa.me/${digits}` : '';
}

function rechtsformLabel(id: string): string {
  if (id === 'gmbh') return 'GmbH';
  if (id === 'gbr') return 'GbR';
  return 'Einzelunternehmen';
}

function statusLabel(id: string): string {
  if (id === 'in_bearbeitung') return 'In Bearbeitung';
  if (id === 'abgeschlossen') return 'Erledigt';
  return 'Neu';
}

function bwaStatus(row: NeukundenIntakeDoc): string {
  if (row.bwaDeletedAt) {
    return `BWA gelöscht am ${new Date(row.bwaDeletedAt).toLocaleDateString('de-DE')}`;
  }
  const active = (row.bwaFiles || []).filter((f) => f.storagePath && !f.deleted);
  if (active.length) {
    const names = active.map((f, i) => f.name || buildBwaFileName(row.inhaber || row.salonName, i, active.length));
    return `Hochgeladen · ${names.join(', ')}`;
  }
  if (row.bwaLater) return 'Nicht hochgeladen · Nachreichen über /bwa-upload';
  return 'Nicht hochgeladen';
}

function isIntakeAdopted(row: NeukundenIntakeDoc): boolean {
  return Boolean(row.vorabkundeId);
}

/** Übernahme nur bei Status „Erledigt“ + Mandant-Nummer + Nettopreis */
function isUebernahmeReady(row: NeukundenIntakeDoc, hasNr: boolean, hasFee: boolean): boolean {
  return row.status === 'abgeschlossen' && hasNr && hasFee;
}

/** Kurzer BWA-Chip für die zugeklappte Zeile */
function bwaChip(row: NeukundenIntakeDoc): { label: string; tone: 'ok' | 'warn' | 'muted' } {
  if (row.bwaDeletedAt) return { label: 'BWA: gelöscht', tone: 'muted' };
  const active = (row.bwaFiles || []).filter((f) => f.storagePath && !f.deleted);
  if (active.length > 0) {
    const shown =
      active.length === 1
        ? active[0].name || buildBwaFileName(row.inhaber || row.salonName)
        : `${active.length} Dateien`;
    return { label: `BWA: ja · ${shown}`, tone: 'ok' };
  }
  if (row.bwaLater) return { label: 'BWA: nein · später', tone: 'warn' };
  return { label: 'BWA: nein', tone: 'warn' };
}

function chipClass(tone: 'ok' | 'warn' | 'muted' | 'danger' | 'info'): string {
  if (tone === 'ok') return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40';
  if (tone === 'warn') return 'bg-amber-500/15 text-amber-300 border-amber-500/40';
  if (tone === 'danger') return 'bg-red-500/15 text-red-300 border-red-500/50';
  if (tone === 'info') return 'bg-sky-500/15 text-sky-300 border-sky-500/40';
  return 'bg-pe-elevated text-pe-muted border-pe-border-strong';
}

function formatEuroAmount(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return `${String(rounded).replace('.', ',')} €`;
}

function formatNettopreis(row: NeukundenIntakeDoc): string {
  if (typeof row.nettopreis === 'number' && Number.isFinite(row.nettopreis)) {
    return formatEuroAmount(row.nettopreis);
  }
  return '';
}

/**
 * Prüft, ob für diesen Mandanten Reverse Charge / Netto-Abbuchung (0 % USt.) gilt:
 * Sobald eine nicht-deutsche USt-IdNr. hinterlegt ist (z. B. ATU..., CHE..., IT..., NL..., BE..., FR..., LU...)
 * oder eine Schweizer IBAN (CH...) vorliegt, erfolgt die Abrechnung/Abbuchung netto (0 % MwSt.).
 */
function isReverseChargeNettoOnly(row: Pick<NeukundenIntakeDoc, 'ustId' | 'iban'>): boolean {
  const ust = String(row?.ustId || '')
    .replace(/\s+/g, '')
    .toUpperCase();
  const iban = String(row?.iban || '')
    .replace(/\s+/g, '')
    .toUpperCase();

  // Schweizer Kunden (Drittland) -> immer 0 %
  if (ust.startsWith('CHE') || iban.startsWith('CH')) {
    return true;
  }

  // Gültige ausländische EU-USt-IdNr. (beginnt mit 2 Buchstaben != DE und mindestens 5 weiteren Zeichen, z. B. ATU63710318)
  if (/^[A-Z]{2}[A-Z0-9]{5,}$/.test(ust) && !ust.startsWith('DE')) {
    return true;
  }

  return false;
}

function nettopreisToBrutto(
  netto: number | null | undefined,
  row?: Pick<NeukundenIntakeDoc, 'ustId' | 'iban'>
): number | null {
  if (typeof netto !== 'number' || !Number.isFinite(netto)) return null;
  if (row && isReverseChargeNettoOnly(row)) {
    return Math.round(netto * 100) / 100;
  }
  return Math.round(netto * (1 + INTAKE_MWST_PCT / 100) * 100) / 100;
}

function formatBruttopreis(row: NeukundenIntakeDoc): string {
  if (isReverseChargeNettoOnly(row)) {
    const netto = nettopreisToBrutto(row.nettopreis, row);
    return netto == null ? 'Netto (0 % Reverse Charge)' : `${formatEuroAmount(netto)} (Netto · 0 %)`;
  }
  const brutto = nettopreisToBrutto(row.nettopreis, row);
  return brutto == null ? '' : formatEuroAmount(brutto);
}

function draftNettopreisValue(row: NeukundenIntakeDoc, draft: Record<string, string>): string {
  const id = row.id || '';
  if (id && Object.prototype.hasOwnProperty.call(draft, id)) return draft[id];
  if (typeof row.nettopreis === 'number' && Number.isFinite(row.nettopreis)) {
    return String(row.nettopreis).replace('.', ',');
  }
  return '';
}

function formatIbanBlocks(raw: string): string {
  const compact = normalizeIban(raw);
  return compact.replace(/(.{4})/g, '$1 ').trim();
}

type IntakeStammdatenDraft = {
  salonName: string;
  rechtsform: IntakeRechtsform;
  firmenbezeichnung: string;
  inhaber: string;
  kontoinhaber: string;
  strasse: string;
  plz: string;
  ort: string;
  iban: string;
  ustId: string;
  email: string;
  whatsapp: string;
  website: string;
  social: string;
  packageType: IntakePackageType;
};

function draftFromRow(row: NeukundenIntakeDoc): IntakeStammdatenDraft {
  return {
    salonName: row.salonName || '',
    rechtsform: row.rechtsform || 'einzel',
    firmenbezeichnung: row.firmenbezeichnung || '',
    inhaber: row.inhaber || '',
    kontoinhaber: row.kontoinhaber || '',
    strasse: row.strasse || '',
    plz: row.plz || '',
    ort: row.ort || '',
    iban: formatIbanBlocks(row.iban || ''),
    ustId: row.ustId || '',
    email: row.email || '',
    whatsapp: row.whatsapp || '',
    website: row.website || '',
    social: row.social || '',
    packageType: row.packageType || 'basic',
  };
}

/** Wer aktuell speichert — DisplayName / E-Mail / Bearbeiter-Fallback */
function resolveEditorLabel(fallbackBearbeiter?: string | null): string {
  const u = auth.currentUser;
  const fromAuth =
    String(u?.displayName || '').trim() ||
    String(u?.email || '')
      .split('@')[0]
      .trim();
  if (fromAuth) return fromAuth;
  const fromRow = String(fallbackBearbeiter || '').trim();
  return fromRow || 'Unbekannt';
}

function contentEditMeta(fallbackBearbeiter?: string | null): {
  lastContentEditedAt: string;
  lastContentEditedBy: string;
} {
  return {
    lastContentEditedAt: new Date().toISOString(),
    lastContentEditedBy: resolveEditorLabel(fallbackBearbeiter),
  };
}

function formatContentEditedLabel(row: NeukundenIntakeDoc): string {
  const at = row.lastContentEditedAt ? Date.parse(row.lastContentEditedAt) : NaN;
  if (!Number.isFinite(at)) return '';
  const when = new Date(at).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const by = String(row.lastContentEditedBy || '').trim() || 'Unbekannt';
  return `Geändert am ${when} · von ${by}`;
}

const EXCEL_FIELDS: { label: string; get: (row: NeukundenIntakeDoc) => string }[] = [
  { label: 'Datum', get: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString('de-DE') : '') },
  { label: 'Salonname', get: (r) => r.salonName || '' },
  { label: 'Rechtsform', get: (r) => rechtsformLabel(r.rechtsform) },
  { label: 'Firmenname', get: (r) => r.firmenbezeichnung || r.salonName || '' },
  { label: 'Mandant', get: (r) => r.inhaber || '' },
  { label: 'Kontoinhaber', get: (r) => r.kontoinhaber || '' },
  { label: 'Straße', get: (r) => r.strasse || '' },
  { label: 'PLZ', get: (r) => r.plz || '' },
  { label: 'Ort', get: (r) => r.ort || '' },
  { label: 'IBAN', get: (r) => r.iban || '' },
  { label: 'USt-IdNr.', get: (r) => r.ustId || '' },
  { label: 'E-Mail', get: (r) => r.email || '' },
  { label: 'WhatsApp', get: (r) => r.whatsapp || '' },
  { label: 'Website', get: (r) => r.website || '' },
  { label: 'Social Media', get: (r) => r.social || '' },
  { label: 'Paket', get: (r) => paketLabel(r.packageType) },
  { label: 'Nettopreis', get: (r) => formatNettopreis(r) },
  { label: 'Bruttopreis', get: (r) => formatBruttopreis(r) },
  { label: 'BWA', get: (r) => bwaStatus(r) },
  { label: 'Bearbeiter', get: (r) => r.bearbeiter || '' },
  { label: 'Status', get: (r) => statusLabel(r.status) },
  { label: 'Mandant-Nummer', get: (r) => r.mandantennummer || '' },
];


const CoachIntakeAdminPage: React.FC<{
  embedded?: boolean;
  onClose?: () => void;
  /** Nur Admin-Bereich: Vorabkunde übernehmen */
  adminMode?: boolean;
}> = ({
  embedded = false,
  onClose,
  adminMode = false,
}) => {
  const [rows, setRows] = useState<NeukundenIntakeDoc[]>([]);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['id']>('alle');
  const [draftNr, setDraftNr] = useState<Record<string, string>>({});
  const [draftFee, setDraftFee] = useState<Record<string, string>>({});
  const [downloadDone, setDownloadDone] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState('');
  const [notice, setNotice] = useState('');
  const [saveFlash, setSaveFlash] = useState<Record<string, string>>({});
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [bwaRows, setBwaRows] = useState<BwaUploadDoc[]>([]);
  const [bwaGot, setBwaGot] = useState<Record<string, boolean>>({});
  const [vorabTarif, setVorabTarif] = useState<Record<string, IntakeTarifTier>>({});
  const [vorabZugang, setVorabZugang] = useState<Record<string, string>>({});
  /** Passwort → Bestätigung → Edit-Panel für Stammdaten (IBAN etc.) */
  const [editUnlockId, setEditUnlockId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<IntakeStammdatenDraft | null>(null);
  const [passwordRowId, setPasswordRowId] = useState<string | null>(null);
  /** Kurzfeedback: welche Zelle gerade kopiert wurde */
  const [copiedCellKey, setCopiedCellKey] = useState('');

  const team = useAppStore((s) => s.team || []);
  const teamRegistry = useAppStore((s) => (s as any).teamRegistry || []);
  const coachPool = useMemo(
    () => getAssignableTeam(mergeTeamPool(team, teamRegistry)),
    [team, teamRegistry]
  );

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'neukunden_intake'), (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data() as NeukundenIntakeDoc;
        return { ...data, id: d.id, bwaFiles: Array.isArray(data.bwaFiles) ? data.bwaFiles : [] };
      });
      list.sort((a, b) => {
        const aAd = isIntakeAdopted(a);
        const bAd = isIntakeAdopted(b);
        if (aAd !== bAd) return aAd ? 1 : -1; // nicht übernommen zuerst
        const aT = a.createdAt ? Date.parse(a.createdAt) : 0;
        const bT = b.createdAt ? Date.parse(b.createdAt) : 0;
        if (aT !== bT) return bT - aT; // neueste oben
        return String(a.inhaber || '').localeCompare(String(b.inhaber || ''), 'de', { sensitivity: 'base' });
      });
      setRows(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'bwa_uploads'), (snap) => {
      const list = snap.docs.map((d) => ({ ...(d.data() as BwaUploadDoc), id: d.id }));
      list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' }));
      setBwaRows(list);
    });
    return () => unsub();
  }, []);

  const visible = useMemo(
    () => (filter === 'alle' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter]
  );

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Eine Datei → PDF direkt; mehrere → ZIP */
  const downloadBwas = async (row: NeukundenIntakeDoc) => {
    if (!row.id || !row.bwaFiles.length) return;
    setBusyId(row.id);
    setNotice('');
    try {
      const files = row.bwaFiles.filter((f) => f.storagePath && !f.deleted);
      if (!files.length) {
        setNotice('Keine BWA-Dateien vorhanden.');
        return;
      }
      const mandantLabel = row.inhaber || row.salonName || 'Mandant';
      if (files.length === 1) {
        const file = files[0];
        const blob = await getBlob(ref(storage, file.storagePath));
        const name = buildBwaFileName(mandantLabel);
        triggerBlobDownload(blob, name);
      } else {
        const zip = new JSZip();
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const blob = await getBlob(ref(storage, file.storagePath));
          zip.file(buildBwaFileName(mandantLabel, i, files.length), blob);
        }
        const out = await zip.generateAsync({ type: 'blob' });
        triggerBlobDownload(out, `BWA_${personFileBase(mandantLabel)}.zip`);
      }
      setDownloadDone((prev) => ({ ...prev, [row.id!]: true }));
    } catch (err: any) {
      setNotice(err?.message || 'Download fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const removeEntry = async (row: NeukundenIntakeDoc) => {
    if (!row.id) return;
    if (!window.confirm(`Anmeldung von ${row.salonName} inklusive BWAs endgültig löschen?`)) return;
    setBusyId(row.id);
    try {
      await deleteNeukundenIntake(row.id, row.bwaFiles);
      setNotice(`Anmeldung von ${row.salonName} gelöscht.`);
    } catch (err: any) {
      setNotice(err?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const purge = async (row: NeukundenIntakeDoc) => {
    if (!row.id || !downloadDone[row.id]) return;
    if (!window.confirm(`BWAs von ${row.salonName} endgültig aus der Cloud löschen?`)) return;
    setBusyId(row.id);
    try {
      await deleteBwaFilesFromStorage(row.id, row.bwaFiles);
      setNotice(`BWAs gelöscht am ${new Date().toLocaleDateString('de-DE')}.`);
    } catch (err: any) {
      setNotice(err?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const closeStammdatenEdit = () => {
    setEditUnlockId(null);
    setEditDraft(null);
    setPasswordRowId(null);
  };

  const handleEditPasswordConfirm = (password: string) => {
    const rowId = passwordRowId;
    setPasswordRowId(null);
    if (!rowId) return;
    if (password !== INTAKE_EDIT_PASSWORD) {
      flashSave(rowId, 'Falsches Passwort — Korrektur abgebrochen.');
      return;
    }
    if (
      !window.confirm(
        'Ja, ich will die Stammdaten dieses Eintrags ändern (z. B. IBAN, Adresse, Kontaktdaten). Fortfahren?'
      )
    ) {
      flashSave(rowId, 'Korrektur abgebrochen.');
      return;
    }
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setEditUnlockId(rowId);
    setEditDraft(draftFromRow(row));
  };

  const saveStammdatenEdit = async (row: NeukundenIntakeDoc) => {
    if (!row.id || !editDraft || editUnlockId !== row.id) return;
    const salonName = editDraft.salonName.trim();
    const inhaber = editDraft.inhaber.trim();
    const email = editDraft.email.trim();
    const iban = normalizeIban(editDraft.iban);
    if (!salonName || !inhaber || !email) {
      flashSave(row.id, 'Salonname, Mandant und E-Mail sind Pflicht.');
      return;
    }
    if (!iban || !isValidIban(iban)) {
      flashSave(row.id, 'IBAN ungültig — bitte prüfen.');
      return;
    }
    setBusyId(row.id);
    try {
      await updateNeukundenIntake(row.id, {
        salonName,
        rechtsform: editDraft.rechtsform,
        firmenbezeichnung: editDraft.firmenbezeichnung.trim(),
        inhaber,
        kontoinhaber: editDraft.kontoinhaber.trim() || inhaber,
        kontoinhaberIdentisch:
          !editDraft.kontoinhaber.trim() ||
          editDraft.kontoinhaber.trim().toLowerCase() === inhaber.toLowerCase(),
        strasse: editDraft.strasse.trim(),
        plz: editDraft.plz.trim(),
        ort: editDraft.ort.trim(),
        iban,
        ustId: editDraft.ustId.trim(),
        email,
        whatsapp: editDraft.whatsapp.trim(),
        website: editDraft.website.trim(),
        social: editDraft.social.trim(),
        packageType: editDraft.packageType,
        ...contentEditMeta(row.bearbeiter),
      });
      flashSave(row.id, 'Stammdaten korrigiert und gespeichert.');
      closeStammdatenEdit();
    } catch (err: any) {
      flashSave(row.id, err?.message || 'Korrektur speichern fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const resolveBetreuerId = (bearbeiter: string | null | undefined): string => {
    if (!bearbeiter) return '';
    const found = coachPool.find(
      (m: any) =>
        String(m.name || '').toLowerCase().includes(String(bearbeiter).toLowerCase()) ||
        String(bearbeiter).toLowerCase().includes(String(m.name || '').split(/\s+/)[0]?.toLowerCase() || '')
    );
    return found?.id || '';
  };

  const takeAsVorabkunde = async (row: NeukundenIntakeDoc) => {
    if (!adminMode || !row.id) return;
    if (row.status !== 'abgeschlossen') {
      setNotice('Übernahme erst möglich, wenn der Status auf „Erledigt“ steht.');
      return;
    }
    const nr = (draftNr[row.id] ?? row.mandantennummer ?? '').trim();
    if (!nr) {
      setNotice('Bitte zuerst eine Mandant-Nummer eintragen.');
      return;
    }
    const feeRaw = draftNettopreisValue(row, draftFee);
    const fee = parseIntakeNettopreis(feeRaw);
    if (fee == null || fee <= 0) {
      setNotice('Bitte zuerst einen Nettopreis (€/Monat) eintragen und speichern.');
      return;
    }
    const tarif = vorabTarif[row.id] || suggestTarifFromPackage(row.packageType);
    const zugangAb = vorabZugang[row.id] || '';
    const letter = resolveCoachInitialLetter(row.bearbeiter || '', coachPool);
    if (!window.confirm(
      `Vorabkunde anlegen?\n\n${row.inhaber} · ${row.salonName}\nTarif: ${tarif}\nNettopreis: ${String(fee).replace('.', ',')} €\nNr. wird auf Vorab-Format normalisiert${letter ? ` (-${letter})` : ''}.`
    )) return;

    setBusyId(row.id);
    setNotice('');
    try {
      // Nettopreis am Intake sichern, bevor Übernahme läuft
      await updateNeukundenIntake(row.id, {
        mandantennummer: nr,
        nettopreis: fee,
      });
      const result = await createVorabkundeFromIntake({
        intake: { ...row, mandantennummer: nr, nettopreis: fee },
        mandantennummer: nr,
        tarif,
        zugangAb: zugangAb || undefined,
        betreuerNameOrId: row.bearbeiter,
        betreuerId: resolveBetreuerId(row.bearbeiter),
        team: coachPool,
        monthlyFeeOverride: fee,
      });
      setDraftNr((prev) => ({ ...prev, [row.id!]: result.mandantNr }));
      setDraftFee((prev) => ({ ...prev, [row.id!]: String(fee).replace('.', ',') }));
      setNotice(`Vorabkunde angelegt: ${result.mandantNr} · ${String(fee).replace('.', ',')} € netto`);
    } catch (err: any) {
      setNotice(err?.message || 'Vorabkunde konnte nicht angelegt werden.');
    } finally {
      setBusyId('');
    }
  };

  const downloadBwaBatch = async () => {
    if (!bwaRows.length) return;
    setBusyId('bwa-batch');
    setNotice('');
    try {
      const withPath = bwaRows.filter((row) => row.storagePath);
      if (withPath.length === 1) {
        const row = withPath[0];
        const blob = await getBlob(ref(storage, row.storagePath));
        triggerBlobDownload(blob, row.fileName || 'BWA.pdf');
      } else {
        const zip = new JSZip();
        for (const row of withPath) {
          const blob = await getBlob(ref(storage, row.storagePath));
          zip.file(row.fileName || `BWA_${row.name}.pdf`, blob);
        }
        const out = await zip.generateAsync({ type: 'blob' });
        triggerBlobDownload(out, 'BWA_Upload.zip');
      }
      setBwaGot((prev) => {
        const next = { ...prev };
        bwaRows.forEach((row) => { if (row.id) next[row.id] = true; });
        return next;
      });
    } catch (err: any) {
      setNotice(err?.message || 'BWA-Download fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const downloadOneBwa = async (row: BwaUploadDoc) => {
    if (!row.id || !row.storagePath) return;
    setBusyId(row.id);
    try {
      const blob = await getBlob(ref(storage, row.storagePath));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.fileName || 'BWA.pdf';
      a.click();
      URL.revokeObjectURL(url);
      setBwaGot((prev) => ({ ...prev, [row.id!]: true }));
    } catch (err: any) {
      setNotice(err?.message || 'Download fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const removeBwa = async (row: BwaUploadDoc) => {
    if (!row.id || !bwaGot[row.id]) return;
    if (!window.confirm(`BWA von ${row.name} löschen?`)) return;
    setBusyId(row.id);
    try {
      await deleteBwaUpload(row.id, row.storagePath);
      setNotice(`BWA von ${row.name} gelöscht.`);
    } catch (err: any) {
      setNotice(err?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setBusyId('');
    }
  };

  const copyCell = async (text: string, ok: string, cellKey?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(ok);
      if (cellKey) {
        setCopiedCellKey(cellKey);
        window.setTimeout(() => {
          setCopiedCellKey((prev) => (prev === cellKey ? '' : prev));
        }, 1600);
      }
    } catch {
      setNotice('Kopieren hat nicht geklappt. Text markieren und Strg+C.');
    }
  };

  const landingUrl = `${window.location.origin}/neukunden`;

  const flashSave = (rowId: string, msg: string) => {
    setNotice(msg);
    setSaveFlash((prev) => ({ ...prev, [rowId]: msg }));
    window.setTimeout(() => {
      setSaveFlash((prev) => {
        if (prev[rowId] !== msg) return prev;
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    }, 3500);
  };

  return (
    <div className={embedded ? 'text-pe-text' : 'min-h-screen bg-pe-bg text-pe-text p-4 sm:p-8'}>
      <PasswordPrompt
        isOpen={!!passwordRowId}
        onClose={() => setPasswordRowId(null)}
        onConfirm={handleEditPasswordConfirm}
        title="Stammdaten korrigieren — Coach-Passwort"
      />
      <div className={embedded ? '' : 'max-w-6xl mx-auto'}>
        {notice ? (
          <div className="sticky top-0 z-30 mb-4 rounded-xl border border-emerald-500/50 bg-emerald-950/95 px-4 py-3 text-sm font-bold text-emerald-100 shadow-lg">
            ✓ {notice}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <p className="text-pe-accent-text text-xs font-black uppercase tracking-widest">Neukunden</p>
            <h1 className="text-2xl font-black">Anmeldungen</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={landingUrl}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-lg bg-[#DCB68A] text-black text-xs font-black uppercase tracking-wider"
            >
              Anmelde-Seite
            </a>
            <a
              href="/bwa-upload"
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-lg border border-[#DCB68A] text-pe-accent-text text-xs font-black uppercase tracking-wider"
            >
              BWA-Seite
            </a>
            <button
              type="button"
              className="px-3 py-2 rounded-lg border border-pe-border-strong text-xs font-black uppercase tracking-wider"
              onClick={() => {
                void navigator.clipboard.writeText(landingUrl).then(() => setNotice('Link zur Anmelde-Seite kopiert.'));
              }}
            >
              Link kopieren
            </button>
            {onClose ? (
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-pe-accent-text underline">
                Schließen
              </button>
            ) : (
              <a href="/coach" className="text-sm text-pe-accent-text underline">Zurück zum Portal</a>
            )}
          </div>
        </div>

        <section className="mb-8 border border-pe-border-strong rounded-2xl p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="font-black">BWA-Upload</h2>
            <button
              type="button"
              disabled={!bwaRows.length || busyId === 'bwa-batch'}
              onClick={() => void downloadBwaBatch()}
              className="px-3 py-2 rounded-lg bg-[#DCB68A] text-black text-xs font-black uppercase disabled:opacity-40"
            >
              Alle BWAs laden
            </button>
          </div>
          {bwaRows.length === 0 && <p className="text-sm text-pe-muted">Keine nachgereichten BWAs.</p>}
          <div className="space-y-2">
            {bwaRows.map((row) => {
              const id = row.id || '';
              return (
                <div key={id} className="flex flex-wrap items-center justify-between gap-2 bg-pe-card rounded-lg px-3 py-2">
                  <div>
                    <div className="font-bold">{row.name}</div>
                    <div className="text-xs text-pe-muted">
                      {row.fileName}
                      {row.kundennummer ? ` · Mandant-Nummer ${row.kundennummer}` : ''}
                      {row.email ? ` · ${row.email}` : ''}
                      {row.createdAt ? ` · ${new Date(row.createdAt).toLocaleDateString('de-DE')}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busyId === id}
                      onClick={() => void downloadOneBwa(row)}
                      className="px-3 py-2 rounded border border-slate-500 text-xs font-black uppercase"
                    >
                      Laden
                    </button>
                    <button
                      type="button"
                      disabled={!bwaGot[id] || busyId === id}
                      onClick={() => void removeBwa(row)}
                      className="px-3 py-2 rounded border border-red-500 text-red-300 text-xs font-black uppercase disabled:opacity-40"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider border ${filter === f.id ? 'bg-[#DCB68A] text-black border-[#DCB68A]' : 'border-pe-border-strong'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {visible.map((row) => {
            const id = row.id || '';
            const deleted = Boolean(row.bwaDeletedAt);
            const adopted = isIntakeAdopted(row);
            // Alle standardmäßig zugeklappt — Status-Chips (inkl. rot) bleiben im Header sichtbar.
            const open = Boolean(openIds[id]);
            const mandant = row.inhaber || 'Ohne Namen';
            const firmenname = row.firmenbezeichnung || row.salonName || '';
            const nrDraft = draftNr[id] ?? row.mandantennummer ?? '';
            const feeDraft = draftNettopreisValue(row, draftFee);
            const bwa = bwaChip(row);
            const hasNr = Boolean(String(nrDraft || '').trim());
            const hasFee = (parseIntakeNettopreis(feeDraft) ?? 0) > 0;
            const uebernahmeReady = isUebernahmeReady(row, hasNr, hasFee);
            return (
              <article
                key={id}
                className={`rounded-2xl p-4 grid gap-3 text-pe-text border ${
                  adopted
                    ? 'bg-pe-input border-pe-border-strong'
                    : 'bg-red-950/25 border-red-500/55 shadow-[0_0_0_1px_rgba(239,68,68,0.15)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }))}
                  className="w-full flex items-start justify-between gap-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <h2 className={`font-black text-lg ${adopted ? 'text-pe-text' : 'text-red-200'}`}>
                      {mandant}
                    </h2>
                    {firmenname && <p className="text-sm text-pe-text/90">{firmenname}</p>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                          adopted ? 'ok' : 'danger'
                        )}`}
                      >
                        {adopted ? 'Übernommen' : 'Nicht übernommen'}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                          row.status === 'abgeschlossen' ? 'ok' : row.status === 'in_bearbeitung' ? 'info' : 'danger'
                        )}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                          bwa.tone
                        )}`}
                      >
                        {bwa.label}
                      </span>
                      {adminMode && (
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                            uebernahmeReady ? 'ok' : 'warn'
                          )}`}
                        >
                          {uebernahmeReady ? 'Übernahme bereit' : 'Übernahme unvollständig'}
                        </span>
                      )}
                      {row.bearbeiter ? (
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                            'muted'
                          )}`}
                        >
                          {row.bearbeiter}
                        </span>
                      ) : null}
                      {row.lastContentEditedAt ? (
                        <span
                          title={formatContentEditedLabel(row)}
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${chipClass(
                            'warn'
                          )}`}
                        >
                          Geändert
                        </span>
                      ) : null}
                    </div>
                  </span>
                  <span className="shrink-0 text-xs font-black uppercase tracking-wider text-pe-accent-text pt-1">
                    {open ? 'Zuklappen' : 'Aufklappen'}
                  </span>
                </button>
                {open && (
                <div className="grid gap-3">
                {formatContentEditedLabel(row) ? (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-950/25 px-3 py-2 text-sm font-semibold text-amber-200">
                    {formatContentEditedLabel(row)}
                  </div>
                ) : null}
                <p className="text-[11px] text-pe-muted">
                  Zelle antippen → nur dieser Inhalt in die Zwischenablage (kein Tabellen-Zeilen-Export).
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {EXCEL_FIELDS.map((field) => {
                    const value = field.get(row);
                    const hasValue = Boolean(String(value || '').trim());
                    const isBrutto = field.label === 'Bruttopreis';
                    const cellKey = `${id}:${field.label}`;
                    const justCopied = copiedCellKey === cellKey;
                    return (
                      <button
                        key={field.label}
                        type="button"
                        title={`${field.label}: Inhalt kopieren`}
                        onClick={() =>
                          void copyCell(
                            value || '',
                            `${field.label}-Inhalt kopiert.`,
                            cellKey
                          )
                        }
                        className={`group relative text-left rounded-lg px-2 py-2 min-h-[4.25rem] border transition-colors ${
                          justCopied
                            ? 'bg-emerald-500/20 border-emerald-400 ring-1 ring-emerald-400/50'
                            : isBrutto
                              ? 'bg-emerald-950/30 border-emerald-500/40 hover:border-emerald-400'
                              : 'bg-pe-card border-pe-border-strong hover:border-[#DCB68A]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div
                            className={`text-[10px] uppercase tracking-wider ${
                              isBrutto ? 'text-emerald-400/80' : 'text-pe-muted'
                            }`}
                          >
                            {isBrutto
                              ? isReverseChargeNettoOnly(row)
                                ? 'Abbuchung · 0 % (Reverse Charge)'
                                : 'Bruttopreis · 19 %'
                              : field.label}
                          </div>
                          <span
                            className={`shrink-0 text-[9px] font-black uppercase tracking-wider ${
                              justCopied
                                ? 'text-emerald-300'
                                : 'text-pe-muted/70 group-hover:text-pe-accent-text'
                            }`}
                          >
                            {justCopied ? '✓ Kopiert' : 'Kopieren'}
                          </span>
                        </div>
                        <div
                          className={`mt-0.5 text-sm break-all select-all ${
                            hasValue
                              ? isBrutto
                                ? 'text-emerald-300 font-bold'
                                : 'text-pe-text font-semibold'
                              : 'text-pe-muted italic'
                          }`}
                        >
                          {hasValue ? value : '—'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {waLink(row.whatsapp) && (
                  <a className="text-pe-accent-text underline text-sm w-fit" href={waLink(row.whatsapp)} target="_blank" rel="noreferrer">WhatsApp öffnen</a>
                )}
                <div className="flex flex-wrap gap-3 items-end">
                  <label className="text-xs uppercase text-pe-muted">Bearbeiter
                    <select
                      className="block mt-1 bg-pe-card border border-pe-border-strong rounded px-2 py-2 text-pe-text"
                      value={row.bearbeiter || ''}
                      onChange={(e) => {
                        const bearbeiter = e.target.value || null;
                        void updateNeukundenIntake(id, {
                          bearbeiter,
                          status: bearbeiter && row.status === 'neu' ? 'in_bearbeitung' : row.status,
                        }).then(() => flashSave(id, bearbeiter ? `Bearbeiter: ${bearbeiter} gespeichert.` : 'Bearbeiter geleert.'));
                      }}
                    >
                      <option value="">—</option>
                      {BEARBEITER.map((name) => <option key={name} value={name}>{name}</option>)}
                    </select>
                  </label>
                  <label className="text-xs uppercase text-pe-muted">Status
                    <select
                      className="block mt-1 bg-pe-card border border-pe-border-strong rounded px-2 py-2 text-pe-text"
                      value={row.status}
                      onChange={(e) => {
                        const status = e.target.value as IntakeStatus;
                        const prev = row.status;
                        void (async () => {
                          try {
                            await updateNeukundenIntake(id, { status });
                            if (status === 'abgeschlossen' && prev !== 'abgeschlossen') {
                              await notifyIntakeErledigt({
                                ...row,
                                status,
                                mandantennummer:
                                  (draftNr[id] ?? row.mandantennummer ?? '').trim() || row.mandantennummer,
                                nettopreis:
                                  parseIntakeNettopreis(draftNettopreisValue(row, draftFee)) ??
                                  row.nettopreis,
                              });
                              flashSave(
                                id,
                                `„${row.salonName || row.inhaber}" erledigt — Admin benachrichtigt.`
                              );
                            } else {
                              flashSave(id, `Status gespeichert: ${statusLabel(status)}.`);
                            }
                          } catch (err: any) {
                            flashSave(id, err?.message || 'Status konnte nicht gespeichert werden.');
                          }
                        })();
                      }}
                    >
                      <option value="neu">Neu</option>
                      <option value="in_bearbeitung">In Bearbeitung</option>
                      <option value="abgeschlossen">Erledigt</option>
                    </select>
                  </label>
                  <label className="text-xs uppercase text-pe-muted">Mandant-Nummer
                    <input
                      className={`block mt-1 bg-pe-card border border-pe-border-strong rounded px-2 py-2 ${
                        String(nrDraft).trim() ? 'text-pe-text font-semibold' : 'text-pe-muted'
                      }`}
                      value={nrDraft}
                      onChange={(e) => setDraftNr((prev) => ({ ...prev, [id]: e.target.value }))}
                    />
                  </label>
                  <label className="text-xs uppercase text-pe-muted">Nettopreis (€/Monat)
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="z. B. 167"
                      className={`block mt-1 w-28 bg-pe-card border border-pe-border-strong rounded px-2 py-2 ${
                        String(feeDraft).trim() ? 'text-pe-text font-semibold' : 'text-pe-muted'
                      }`}
                      value={feeDraft}
                      onChange={(e) => setDraftFee((prev) => ({ ...prev, [id]: e.target.value }))}
                    />
                  </label>
                  {(() => {
                    const feeParsed = parseIntakeNettopreis(feeDraft);
                    const isRevCharge = isReverseChargeNettoOnly(row);
                    const brutto = nettopreisToBrutto(feeParsed, row);
                    const bruttoLabel = brutto == null ? '—' : formatEuroAmount(brutto);
                    return (
                      <button
                        type="button"
                        title={
                          isRevCharge
                            ? 'Reverse Charge (0 % USt.) — Netto-Abbuchungsbetrag kopieren'
                            : 'Brutto (19 % MwSt) kopieren — für Bank-Abbuchung'
                        }
                        onClick={() => {
                          if (brutto == null) return;
                          void copyCell(
                            bruttoLabel,
                            isRevCharge
                              ? `Netto-Abbuchung (0 % Reverse Charge) ${bruttoLabel} kopiert.`
                              : `Bruttopreis ${bruttoLabel} kopiert.`
                          );
                        }}
                        className="text-left"
                      >
                        <span className="text-xs uppercase text-pe-muted">
                          {isRevCharge ? 'Abbuchung (0 % Reverse Charge)' : 'Bruttopreis (19 %)'}
                        </span>
                        <span
                          className={`mt-1 flex min-w-[7rem] px-2.5 items-center rounded border border-emerald-500/40 bg-emerald-950/30 py-2 text-sm font-bold select-all ${
                            brutto == null ? 'text-pe-muted' : 'text-emerald-300'
                          }`}
                        >
                          {isRevCharge && brutto != null ? `${bruttoLabel} (Netto)` : bruttoLabel}
                        </span>
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    className="px-3 py-2 rounded bg-[#DCB68A] text-black text-xs font-black uppercase"
                    onClick={() => {
                      const feeParsed = parseIntakeNettopreis(draftNettopreisValue(row, draftFee));
                      const nrVal = (draftNr[id] ?? row.mandantennummer ?? '').trim() || null;
                      if (draftNettopreisValue(row, draftFee).trim() && feeParsed == null) {
                        flashSave(id, 'Nettopreis ungültig — bitte Zahl eingeben (z. B. 167 oder 94,50).');
                        return;
                      }
                      void updateNeukundenIntake(id, {
                        mandantennummer: nrVal,
                        nettopreis: feeParsed,
                        ...contentEditMeta(row.bearbeiter),
                      }).then(() => {
                        flashSave(
                          id,
                          feeParsed != null
                            ? `Gespeichert ✓ · Nr. ${nrVal || '—'} · ${String(feeParsed).replace('.', ',')} €`
                            : `Gespeichert ✓ · Nr. ${nrVal || '—'}`
                        );
                      }).catch((err: any) => {
                        flashSave(id, err?.message || 'Speichern fehlgeschlagen.');
                      });
                    }}
                  >
                    Speichern
                  </button>
                  {saveFlash[id] ? (
                    <span className="text-sm font-bold text-emerald-400">{saveFlash[id]}</span>
                  ) : null}
                </div>
                {editUnlockId === id && editDraft ? (
                  <div className="rounded-xl border border-amber-500/40 bg-amber-950/20 p-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">
                        Stammdaten korrigieren (Passwort freigeschaltet)
                      </p>
                      <button
                        type="button"
                        className="text-[10px] font-bold uppercase tracking-wider text-pe-muted hover:text-pe-text"
                        onClick={closeStammdatenEdit}
                      >
                        Abbrechen
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {(
                        [
                          ['salonName', 'Salonname'],
                          ['firmenbezeichnung', 'Firmenname'],
                          ['inhaber', 'Mandant'],
                          ['kontoinhaber', 'Kontoinhaber'],
                          ['strasse', 'Straße'],
                          ['plz', 'PLZ'],
                          ['ort', 'Ort'],
                          ['iban', 'IBAN'],
                          ['ustId', 'USt-IdNr.'],
                          ['email', 'E-Mail'],
                          ['whatsapp', 'WhatsApp'],
                          ['website', 'Website'],
                          ['social', 'Social Media'],
                        ] as const
                      ).map(([key, label]) => (
                        <label key={key} className="text-[10px] uppercase text-pe-muted">
                          {label}
                          <input
                            className={`mt-1 block w-full rounded border border-pe-border-strong bg-pe-card px-2 py-1.5 text-sm text-pe-text ${
                              key === 'iban' ? 'font-mono' : ''
                            }`}
                            value={editDraft[key]}
                            onChange={(e) =>
                              setEditDraft((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      [key]:
                                        key === 'iban' ? formatIbanBlocks(e.target.value) : e.target.value,
                                    }
                                  : prev
                              )
                            }
                          />
                        </label>
                      ))}
                      <label className="text-[10px] uppercase text-pe-muted">
                        Rechtsform
                        <select
                          className="mt-1 block w-full rounded border border-pe-border-strong bg-pe-card px-2 py-1.5 text-sm text-pe-text"
                          value={editDraft.rechtsform}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev
                                ? { ...prev, rechtsform: e.target.value as IntakeRechtsform }
                                : prev
                            )
                          }
                        >
                          <option value="einzel">Einzelunternehmen</option>
                          <option value="gmbh">GmbH</option>
                          <option value="gbr">GbR</option>
                        </select>
                      </label>
                      <label className="text-[10px] uppercase text-pe-muted">
                        Paket
                        <select
                          className="mt-1 block w-full rounded border border-pe-border-strong bg-pe-card px-2 py-1.5 text-sm text-pe-text"
                          value={editDraft.packageType}
                          onChange={(e) =>
                            setEditDraft((prev) =>
                              prev
                                ? { ...prev, packageType: e.target.value as IntakePackageType }
                                : prev
                            )
                          }
                        >
                          <option value="insider">INSIDER</option>
                          <option value="insider_solo">INSIDER Solo</option>
                          <option value="basic">BASIC</option>
                        </select>
                      </label>
                    </div>
                    <button
                      type="button"
                      disabled={busyId === id}
                      onClick={() => void saveStammdatenEdit(row)}
                      className="px-3 py-2 rounded bg-amber-400 text-black text-xs font-black uppercase disabled:opacity-40"
                    >
                      Korrektur speichern
                    </button>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!id || busyId === id || editUnlockId === id}
                    onClick={() => setPasswordRowId(id)}
                    className="px-3 py-2 rounded border border-amber-500/60 text-amber-300 text-xs font-black uppercase disabled:opacity-40"
                  >
                    Stammdaten korrigieren
                  </button>
                  <button
                    type="button"
                    disabled={!row.bwaFiles.length || busyId === id || deleted}
                    onClick={() => void downloadBwas(row)}
                    className="px-3 py-2 rounded border border-slate-500 text-xs font-black uppercase disabled:opacity-40"
                  >
                    {(row.bwaFiles || []).filter((f) => f.storagePath && !f.deleted).length > 1
                      ? 'BWAs laden (.zip)'
                      : 'BWA laden (.pdf)'}
                  </button>
                  <button
                    type="button"
                    disabled={!downloadDone[id] || deleted || busyId === id}
                    onClick={() => void purge(row)}
                    className="px-3 py-2 rounded border border-red-500 text-red-300 text-xs font-black uppercase disabled:opacity-40"
                  >
                    BWAs aus Cloud löschen
                  </button>
                  <button
                    type="button"
                    disabled={!id || busyId === id}
                    onClick={() => void removeEntry(row)}
                    className="px-3 py-2 rounded border border-red-500 text-red-300 text-xs font-black uppercase disabled:opacity-40"
                  >
                    Eintrag löschen
                  </button>
                </div>
                {adminMode && (
                  <div className="mt-1 rounded-xl border border-[#DCB68A]/40 bg-pe-card p-3 space-y-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-pe-accent-text">
                      Admin · Als Vorabkunde übernehmen
                    </div>
                    {row.vorabkundeId ? (
                      <p className="text-sm text-emerald-400">
                        Bereits als Vorabkunde angelegt (ID: {row.vorabkundeId}).
                      </p>
                    ) : (
                      <>
                        {(() => {
                          const nrReady = Boolean(
                            (draftNr[id] ?? row.mandantennummer ?? '').trim()
                          );
                          const feeReady =
                            (parseIntakeNettopreis(draftNettopreisValue(row, draftFee)) ?? 0) > 0;
                          const statusReady = row.status === 'abgeschlossen';
                          const canTakeOver = statusReady && nrReady && feeReady && busyId !== id;
                          return (
                        <div className="flex flex-wrap gap-3 items-end">
                          <label className="text-xs uppercase text-pe-muted">
                            Tarif
                            <select
                              className="block mt-1 bg-pe-input border border-pe-border-strong rounded px-2 py-2 text-pe-text"
                              value={vorabTarif[id] || suggestTarifFromPackage(row.packageType)}
                              onChange={(e) =>
                                setVorabTarif((prev) => ({
                                  ...prev,
                                  [id]: e.target.value as IntakeTarifTier,
                                }))
                              }
                            >
                              {TARIF_OPTIONS.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="text-xs uppercase text-pe-muted">
                            Zugang ab
                            <input
                              type="month"
                              className="block mt-1 bg-pe-input border border-pe-border-strong rounded px-2 py-2 text-pe-text [color-scheme:dark]"
                              value={
                                vorabZugang[id] ||
                                `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
                              }
                              onChange={(e) =>
                                setVorabZugang((prev) => ({ ...prev, [id]: e.target.value }))
                              }
                            />
                          </label>
                          <button
                            type="button"
                            disabled={!canTakeOver}
                            onClick={() => void takeAsVorabkunde(row)}
                            className="px-3 py-2 rounded bg-[#DCB68A] text-black text-xs font-black uppercase disabled:opacity-40"
                            title={
                              !statusReady
                                ? 'Status muss „Erledigt“ sein'
                                : !nrReady
                                  ? 'Mandant-Nummer fehlt'
                                  : !feeReady
                                    ? 'Nettopreis fehlt'
                                    : 'Vorabkunde anlegen'
                            }
                          >
                            Vorabkunde anlegen
                          </button>
                        </div>
                          );
                        })()}
                        {!uebernahmeReady && (
                          <p className="text-[11px] text-amber-400/90">
                            Übernahme blockiert: Status „Erledigt“, Mandant-Nummer und Nettopreis müssen gesetzt sein.
                          </p>
                        )}
                        <p className="text-[11px] text-pe-muted">
                          Übernimmt Stammdaten + Nettopreis. Mandant-Nummer wird auf <span className="font-mono text-pe-muted">MA NN/YYYY-X</span> normalisiert. Solo/Team nur als Badge, nicht in der Nummer.
                          {' '}Nur Admin — manuelle Coach-Zuweisung im Admin-Center.
                        </p>
                      </>
                    )}
                  </div>
                )}
                {deleted && (
                  <p className="text-xs text-pe-muted">BWAs heruntergeladen und aus Cloud-Storage entfernt.</p>
                )}
                </div>
                )}
              </article>
            );
          })}
          {visible.length === 0 && <p className="text-pe-muted">Keine Einträge.</p>}
        </div>
      </div>
    </div>
  );
};

export const IntakeHubModal: React.FC<{ onClose: () => void; adminMode?: boolean }> = ({
  onClose,
  adminMode = false,
}) => (
  <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm overflow-y-auto" role="dialog" aria-modal="true">
    <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
      <div className="w-full max-w-6xl bg-pe-bg border border-pe-border-strong rounded-2xl p-4 sm:p-6 my-4 shadow-2xl">
        <CoachIntakeAdminPage embedded adminMode={adminMode} onClose={onClose} />
      </div>
    </div>
  </div>
);

export default CoachIntakeAdminPage;
